import React from 'react';
import { useAuth } from '@/hooks/use-auth';
import { isSupabaseConfigured } from '@/lib/supabase';
import { AuthContext } from '@/hooks/use-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  // Show a configuration warning if Supabase is not configured
  if (!isSupabaseConfigured()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-2xl w-full space-y-6">
          <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-lg border border-[var(--node-border)] bg-[var(--node-bg)] flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6" />
                </div>
              </div>
              <CardTitle className="text-2xl text-orange-900 dark:text-orange-100">
                Supabase Configuration Required
              </CardTitle>
              <CardDescription className="text-orange-800 dark:text-orange-200">
                To use this application, you need to configure your Supabase credentials.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-orange-300 bg-orange-100 dark:border-orange-700 dark:bg-orange-900/30">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800 dark:text-orange-200">
                  <div className="space-y-3">
                    <p className="font-medium">Steps to configure Supabase:</p>
                    <ol className="space-y-2 list-decimal list-inside text-sm">
                      <li>Create a new project at <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">supabase.com</a></li>
                      <li>Go to Settings → API in your Supabase dashboard</li>
                      <li>Copy your Project URL and anon public key</li>
                      <li>Update your .env file with these values:</li>
                    </ol>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="bg-muted p-4 rounded-lg font-mono text-sm">
                <div className="space-y-1">
                  <div className="text-muted-foreground"># Add these to your .env file:</div>
                  <div>VITE_SUPABASE_URL=your_project_url_here</div>
                  <div>VITE_SUPABASE_ANON_KEY=your_anon_key_here</div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  <strong>Important:</strong> After updating your .env file, restart the development server for changes to take effect.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => window.location.reload()}
                    className="flex items-center space-x-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Refresh Page</span>
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => window.open('https://supabase.com/dashboard', '_blank')}
                    className="flex items-center space-x-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Open Supabase Dashboard</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-dashed">
            <CardContent className="p-6 text-center">
              <div className="space-y-2">
                <h3 className="font-medium">Need Help?</h3>
                <p className="text-sm text-muted-foreground">
                  Check the .env.example file for the required format, or visit our documentation for detailed setup instructions.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
